import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { extractErrorMessage } from '../lib/errors';

interface BriefingProps {
  password: string;
  refreshKey: number;
}

interface FalarAgora {
  empresa: string;
  contato: string | null;
  cargo: string | null;
  whatsapp: string;
  motivo: string;
  canal: string;
}

interface FecharNegocio {
  empresa: string;
  cidade: string | null;
  valor_potencial: number | null;
  etapa: string;
  proxima_acao: string;
  whatsapp: string;
  contato: string | null;
}

interface ObraNova {
  contratante: string;
  cidade: string | null;
  uf: string | null;
  segmento: string | null;
  valor: number | null;
  em_andamento: boolean;
  prioridade: string | null;
  profissional: string | null;
}

interface NoticiaQuente {
  titulo: string;
  cidade: string | null;
  fonte: string | null;
  score: number;
  url_fonte: string | null;
  descricao: string | null;
}

interface BriefingData {
  saudacao: string;
  abertura: string;
  resumo: {
    falar: number;
    fechar: number;
    obras: number;
    noticias: number;
  };
  falar_agora: FalarAgora[];
  fechar: FecharNegocio[];
  obras_novas: ObraNova[];
  noticias: NoticiaQuente[];
}

function formatBRL(value: number | null): string {
  const n = Number(value ?? 0);
  if (!Number.isFinite(n) || n <= 0) return 'valor a definir';
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });
}

function waLink(phone: string, text: string): string | null {
  const digits = (phone ?? '').replace(/\D/g, '');
  if (!digits) return null;
  const full = digits.length === 10 || digits.length === 11 ? `55${digits}` : digits;
  return `https://wa.me/${full}?text=${encodeURIComponent(text)}`;
}

export default function Briefing({ password, refreshKey }: BriefingProps) {
  const [data, setData] = useState<BriefingData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: resp, error: invokeErr } = await supabase.functions.invoke('crm-ai', {
        body: { action: 'briefing', password },
      });
      if (invokeErr) throw new Error(await extractErrorMessage(invokeErr));
      const parsed = resp as BriefingData & { error?: string };
      if (parsed?.error) throw new Error(parsed.error);
      setData(parsed);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao montar o briefing.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  if (loading && !data) {
    return (
      <div className="flex justify-center py-10">
        <div className="w-8 h-8 border-2 border-[#E8610A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3 flex items-center justify-between gap-3">
        <span>{error}</span>
        <button
          onClick={load}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-red-700 hover:underline cursor-pointer whitespace-nowrap"
        >
          <i className="ri-refresh-line"></i>
          Tentar de novo
        </button>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="space-y-5">
      {/* Abertura — a IA "falando" com o Ednilson */}
      <div className="rounded-2xl border border-[#E8610A]/30 bg-gradient-to-br from-[#FFF7F0] to-white p-5 md:p-6">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 shrink-0 flex items-center justify-center bg-[#E8610A] text-white rounded-2xl">
            <i className="ri-sparkling-2-line text-xl"></i>
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-lg md:text-xl font-bold text-gray-900">
              {data.saudacao}, Ednilson. 👋
            </h2>
            <p className="text-sm md:text-[15px] text-gray-700 mt-2 leading-relaxed whitespace-pre-line">
              {data.abertura}
            </p>
          </div>
          <button
            onClick={load}
            disabled={loading}
            title="Atualizar briefing"
            className="shrink-0 inline-flex items-center justify-center w-9 h-9 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
          >
            <i className={`ri-refresh-line ${loading ? 'animate-spin' : ''}`}></i>
          </button>
        </div>
      </div>

      {/* Fechar negócio — prioridade máxima */}
      {data.fechar.length > 0 && (
        <section className="bg-white rounded-2xl border border-hop-border p-5">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-green-50 text-green-600">
              <i className="ri-hand-coin-line"></i>
            </div>
            <h3 className="font-bold text-gray-900">Fechar negócio</h3>
            <span className="text-xs font-bold text-green-700 bg-green-50 rounded-full px-2 py-0.5">
              {data.fechar.length}
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {data.fechar.map((f, i) => (
              <div key={i} className="flex items-start gap-3 rounded-xl border border-green-200 bg-green-50/40 p-3.5">
                <div className="w-10 h-10 shrink-0 flex items-center justify-center bg-green-500 text-white rounded-xl text-sm font-bold">
                  {formatBRL(f.valor_potencial).replace(/\D/g, '').slice(0, 2)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">{f.empresa}</p>
                  <p className="text-xs text-gray-600 mt-0.5">
                    <span className="font-semibold text-green-700">{formatBRL(f.valor_potencial)}</span>
                    {f.cidade ? ` · ${f.cidade}` : ''} · {f.etapa}
                  </p>
                  <p className="text-xs text-gray-500 mt-1 line-clamp-2">{f.proxima_acao}</p>
                  {f.whatsapp && (
                    <a
                      href={waLink(f.whatsapp, `Olá${f.contato ? ' ' + f.contato : ''}! Tudo bem? Sobre ${f.empresa}...`)!}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 mt-2 text-xs font-semibold text-[#128C7E] hover:underline cursor-pointer"
                    >
                      <i className="ri-whatsapp-line"></i>
                      Chamar no WhatsApp
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Falar agora */}
      {data.falar_agora.length > 0 && (
        <section className="bg-white rounded-2xl border border-hop-border p-5">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#E8610A]/10 text-[#E8610A]">
              <i className="ri-user-voice-line"></i>
            </div>
            <h3 className="font-bold text-gray-900">Fale com essas pessoas</h3>
            <span className="text-xs font-bold text-[#E8610A] bg-[#E8610A]/10 rounded-full px-2 py-0.5">
              {data.falar_agora.length}
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {data.falar_agora.map((f, i) => (
              <div key={i} className="flex items-center gap-3 rounded-xl border border-hop-border bg-hop-bg-alt p-3.5">
                <div className="w-10 h-10 shrink-0 flex items-center justify-center bg-white text-gray-600 rounded-xl border border-hop-border">
                  <i className="ri-user-line text-lg"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">
                    {f.contato || f.empresa}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {f.empresa}{f.cargo ? ` · ${f.cargo}` : ''}
                  </p>
                  <p className="text-xs text-gray-600 mt-0.5 line-clamp-1">{f.motivo}</p>
                </div>
                {f.whatsapp && (
                  <a
                    href={waLink(f.whatsapp, `Olá${f.contato ? ' ' + f.contato : ''}! Tudo bem?`)!}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="shrink-0 inline-flex items-center justify-center w-9 h-9 bg-[#25D366]/10 text-[#128C7E] rounded-lg hover:bg-[#25D366]/20 transition-colors cursor-pointer"
                    title="Chamar no WhatsApp"
                  >
                    <i className="ri-whatsapp-line text-lg"></i>
                  </a>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Obras novas + Notícias quentes */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-start">
        {data.obras_novas.length > 0 && (
          <section className="bg-white rounded-2xl border border-hop-border p-5">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-50 text-amber-600">
                <i className="ri-building-2-line"></i>
              </div>
              <h3 className="font-bold text-gray-900">Obras novas no radar</h3>
              <span className="text-xs font-bold text-amber-700 bg-amber-50 rounded-full px-2 py-0.5">
                {data.obras_novas.length}
              </span>
            </div>
            <div className="space-y-2.5">
              {data.obras_novas.map((o, i) => (
                <div key={i} className="flex items-start gap-3 rounded-xl border border-hop-border bg-white p-3.5">
                  <div className="w-9 h-9 shrink-0 flex items-center justify-center bg-amber-50 text-amber-600 rounded-lg">
                    <i className="ri-tools-line"></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">{o.contratante}</p>
                    <p className="text-xs text-gray-500">
                      {[o.cidade, o.uf].filter(Boolean).join(' — ')}
                      {o.segmento ? ` · ${o.segmento}` : ''}
                    </p>
                    <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                      {o.valor != null && (
                        <span className="text-xs font-semibold text-gray-700">{formatBRL(o.valor)}</span>
                      )}
                      {o.em_andamento && (
                        <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 bg-amber-100 rounded-full px-2 py-0.5">
                          <i className="ri-hourglass-2-line"></i>
                          Em andamento
                        </span>
                      )}
                      {o.prioridade && (
                        <span className="text-[11px] font-semibold text-gray-500">{o.prioridade}</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {data.noticias.length > 0 && (
          <section className="bg-white rounded-2xl border border-hop-border p-5">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                <i className="ri-newspaper-line"></i>
              </div>
              <h3 className="font-bold text-gray-900">Notícias quentes</h3>
              <span className="text-xs font-bold text-blue-700 bg-blue-50 rounded-full px-2 py-0.5">
                {data.noticias.length}
              </span>
            </div>
            <div className="space-y-2.5">
              {data.noticias.map((n, i) => (
                <div key={i} className="flex items-start gap-3 rounded-xl border border-hop-border bg-white p-3.5">
                  <div className="w-9 h-9 shrink-0 flex items-center justify-center bg-blue-50 text-blue-600 rounded-lg">
                    <i className="ri-megaphone-line"></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 leading-snug">{n.titulo}</p>
                    {n.descricao && (
                      <p className="text-xs text-gray-600 mt-0.5 line-clamp-2">{n.descricao}</p>
                    )}
                    <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                      {n.cidade && <span className="text-[11px] text-gray-500">{n.cidade}</span>}
                      {n.fonte && <span className="text-[11px] text-gray-400">· {n.fonte}</span>}
                      {n.score != null && (
                        <span className="text-[11px] font-bold text-blue-700 bg-blue-50 rounded-full px-2 py-0.5">
                          {n.score} pts
                        </span>
                      )}
                      {n.url_fonte && (
                        <a
                          href={n.url_fonte}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[11px] font-semibold text-blue-600 hover:underline cursor-pointer"
                        >
                          Ver fonte ↗
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}